import {
  App,
  Button as ButtonAnt,
  Col,
  Divider,
  Empty,
  Form,
  FormProps,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Tabs,
  TabsProps,
  Tooltip
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import ReactQuill from 'react-quill';
import { Button } from '../../button';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ConclusionRecommendationKey,
  ConclusionRecommendationAgeKey
} from '@/enums/conclusion-recommendation-key';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  getConclusionRecommendation,
  resetMessages,
  updateConclusionRecommendation,
  watchConclusionRecommendationList,
  watchConclusionRecommendationLoading,
  watchConclusionRecommendationMessage
} from '@/redux/slices/conclusion-recommendation.slice';
import { setMessages } from '@/redux/slices/app.slice';
import {
  isKeyGenderRelated,
  isShowBMIIndiCatorRelated
} from '@/helpers/conclusion-recommendation.helper';
import { isKeyAgeRelated } from '@/helpers/conclusion-recommendation.helper';
import { valueSelectedModelOne } from '@/helpers/conclusion-recommendation.helper';
import { valueSelectedModelTwo } from '@/helpers/conclusion-recommendation.helper';
import { bloodPressureInput } from '@/helpers/conclusion-recommendation.helper';
import {
  isIndicatorOne,
  isIndicatorTwo,
  optionAgeType
} from '@/helpers/conclusion-recommendation.helper';
import { excludeOptionAgeRelated } from '@/helpers/conclusion-recommendation.helper';
import { notShowAge } from '@/helpers/conclusion-recommendation.helper';
import usePermission from '@/hooks/permission.hook';
import { useBeforeUnload } from 'react-router-dom';
import {
  getConclusionDropdown,
  watchConclusionDropdownList
} from '@/redux/slices/conclusion-dropdown.slice';

type ConclusionRecommendationFormProps = {
  selectedKey: string;
};

interface FormData extends BaseFields {
  id: number;
  isEditable: boolean;
}

type FieldType = Partial<BaseFields> & { [key: string]: any };

interface BaseFields {
  age?: string;
  SBP?: string;
  SBPOperator?: string;
  SBPStart?: number;
  SBPEnd?: number;
  DBP?: string;
  DBPOperator?: string;
  DBPStart?: number;
  DBPEnd?: number;
  classification?: string;
  conclusion?: string;
  recommendation?: string;
  radioSelection?: string;
  gender?: string;
}

export const ConclusionRecommendationForm = ({
  selectedKey = ConclusionRecommendationKey.BloodPressureClinic
}: ConclusionRecommendationFormProps) => {
  const { modal } = App.useApp();
  const dispatch = useAppDispatch();
  const conclusionRecommendationList = useAppSelector(
    watchConclusionRecommendationList
  );
  const messageInfo = useAppSelector(watchConclusionRecommendationMessage);
  const loading = useAppSelector(watchConclusionRecommendationLoading);
  const conclusionDropdowns = useAppSelector(watchConclusionDropdownList);
  const { creatable, updatable, deletable } = usePermission();

  const formats = [
    'header',
    'bold', 'italic', 'underline',
    'link',
    'blockquote', 'list', 'bullet',
    'color', 'background'
  ];

  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline'],
      ['link'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ color: [] }, { background: [] }],
      ['clean']
    ]
  };

  const [form] = Form.useForm();
  const [formData, setFormData] = useState<FormData[]>([]);
  const [model, setModel] = useState('HOSPITAL');
  const [ageModel, setAgeModel] = useState('FROM_5_LESS_THAN_12');
  const [gender, setGender] = useState('nam');
  const [ageType, setAgeType] = useState('FROM_5_LESS_THAN_12');

  const [typeOptions, setTypeOptions] = useState<any>([]);
  const [itemFollowAges, setItemFollowAges] = useState<any>([]);
  const [operatorsOptions, setOperatorsOptions] = useState<any>([]);
  const [ageOptions, setAgeOptions] = useState<any>([]);
  const [sbpOperatorValues, setSbpOperatorValues] = useState<
    Record<string, string>
  >({});
  const [dbpOperatorValues, setDbpOperatorValues] = useState<
    Record<string, string>
  >({});

  const items: TabsProps['items'] = useMemo(() => {
    if (
      ageModel === 'FROM_20_LESS_THEN_70' ||
      ageModel === 'EQUAL_MORE_THAN_70'
    ) {
      setGender('nam');
      return [];
    }
    return [
      {
        key: 'nam',
        label: 'Đối với nam'
      },
      {
        key: 'nu',
        label: 'Đối với nữ'
      }
    ];
  }, [ageModel]);
  let showAge = useMemo(
    () => isKeyAgeRelated(selectedKey as ConclusionRecommendationKey),
    [selectedKey]
  );
  const showGender = useMemo(
    () => isKeyGenderRelated(selectedKey as ConclusionRecommendationKey),
    [selectedKey]
  );
  const bloodPressureLabelOne = useMemo(
    () => valueSelectedModelOne(selectedKey as ConclusionRecommendationKey),
    [selectedKey]
  );
  const bloodPressureLabelTwo = useMemo(
    () => valueSelectedModelTwo(selectedKey as ConclusionRecommendationKey),
    [selectedKey]
  );

  const fetchData = useCallback(() => {
    switch (selectedKey) {
      case ConclusionRecommendationKey.BloodPressureClinic:
        setModel('HOSPITAL');
        dispatch(getConclusionRecommendation({ model: 'HOSPITAL' }));
        break;
      case ConclusionRecommendationKey.BloodPressureHome:
        setModel('HOME');
        dispatch(getConclusionRecommendation({ model: 'HOME' }));
        break;
      case ConclusionRecommendationKey.BloodSugarFasting:
        setModel('HUNGRY');
        dispatch(getConclusionRecommendation({ model: 'HUNGRY' }));
        break;
      case ConclusionRecommendationKey.BloodSugar2H:
        setModel('2_HOURS');
        dispatch(getConclusionRecommendation({ model: '2_HOURS' }));
        break;
      case ConclusionRecommendationKey.BloodSugarHbA1c:
        setModel('HBA1C');
        dispatch(getConclusionRecommendation({ model: 'HBA1C' }));
        break;
      case ConclusionRecommendationKey.LiverEnzymeSgot:
        setModel('SGOT');
        dispatch(getConclusionRecommendation({ model: 'SGOT', gender }));
        break;
      case ConclusionRecommendationKey.LiverEnzymeSgpt:
        setModel('SGPT');
        dispatch(getConclusionRecommendation({ model: 'SGPT', gender }));
        break;
      case ConclusionRecommendationKey.LipidCholesterol:
        setModel('CHOL');
        dispatch(getConclusionRecommendation({ model: 'CHOL' }));
        break;
      case ConclusionRecommendationKey.LipidLdl:
        setModel('LDL');
        dispatch(getConclusionRecommendation({ model: 'LDL' }));
        break;
      case ConclusionRecommendationKey.LipidHdl:
        setModel('HDL');
        dispatch(getConclusionRecommendation({ model: 'HDL' }));
        break;
      case ConclusionRecommendationKey.LipidTriglyceride:
        setModel('TRI');
        dispatch(getConclusionRecommendation({ model: 'TRI' }));
        break;
      case ConclusionRecommendationKey.Urea:
        setModel('URE');
        dispatch(getConclusionRecommendation({ model: 'URE' }));
        break;
      case ConclusionRecommendationKey.Creatinine:
        setModel('CREA');
        dispatch(getConclusionRecommendation({ model: 'CREA', gender }));
        break;
      case ConclusionRecommendationKey.UricAcid:
        setModel('AXIT_URIC');
        dispatch(getConclusionRecommendation({ model: 'AXIT_URIC', gender }));
        break;
      case ConclusionRecommendationKey.BMI:
        setModel('BMI');
        if (ageModel === 'FROM_20_LESS_THEN_70' || ageModel === 'EQUAL_MORE_THAN_70') {
          dispatch(
            getConclusionRecommendation({
              model: 'BMI',
              ageType: ageModel
            })
          );
        } else {
          dispatch(
            getConclusionRecommendation({
              model: 'BMI',
              gender,
              ageType: ageModel
            })
          );
        }
        break;
      case ConclusionRecommendationKey.HeightLength:
        setModel('HEIGHT');
        dispatch(getConclusionRecommendation({ model: 'HEIGHT', gender }));
        break;
      case ConclusionRecommendationKey.Weight:
        setModel('WEIGHT');
        dispatch(
          getConclusionRecommendation({
            model: 'WEIGHT',
            gender,
            ageType: 'FROM_0_LESS_THAN_5'
          })
        );
        break;
      case ConclusionRecommendationKey.WeightForHeight:
        setModel('WEIGHT_HEIGHT');
        dispatch(
          getConclusionRecommendation({ model: 'WEIGHT_HEIGHT', gender })
        );
        break;
      case ConclusionRecommendationKey.Weight5To10:
        setModel('WEIGHT_5_19');
        dispatch(
          getConclusionRecommendation({ model: 'WEIGHT_5_19', gender })
        );
        break;


      case ConclusionRecommendationKey.Height5To16: {
        setModel('HEIGHT_5_19');
        dispatch(
          getConclusionRecommendation({ model: 'HEIGHT_5_19', gender, ageType: (ageModel === 'FROM_5_LESS_THAN_12' || ageModel === 'FROM_5_LESS_THAN_10') ? 'FROM_5_LESS_THAN_10' : 'FROM_10_LESS_THAN_16' })
        );
        break;
      }
      default:
        setModel('HOSPITAL');
        dispatch(getConclusionRecommendation({ model: 'HOSPITAL' }));
        break;
    }
    const isBMI =
      isKeyAgeRelated(selectedKey as ConclusionRecommendationKey) &&
      isShowBMIIndiCatorRelated(ageModel);
    const isHeight10_16 =
      isKeyAgeRelated(selectedKey as ConclusionRecommendationKey) &&
      (ageModel === 'FROM_10_LESS_THAN_16');
    setTypeOptions(
      conclusionDropdowns
        .filter((item) => item.type === (isBMI || isHeight10_16 ? 'TYPE_1' : 'TYPE'))
        .map((item) => ({
          key: item.code,
          label: item.name,
          value: item.code
        }))
    );
    setItemFollowAges(
      conclusionDropdowns
        .filter((item) => item.type === 'AGE_TYPE')
        .map((item) => ({
          key: item.code,
          label: item.name,
          value: item.code
        }))
    );
    setOperatorsOptions(
      conclusionDropdowns
        .filter((item) => item.type === 'INDICATOR')
        .map((item) => ({
          key: item.code,
          label: item.name,
          value: item.code
        }))
    );
    setAgeOptions(
      conclusionDropdowns
        .filter((item) => item.type === 'AGE_TYPE')
        .map((item) => ({
          label: item.name,
          value: item.code
        }))
    );
  }, [conclusionDropdowns, selectedKey, dispatch, gender, ageModel]);

  const handleAgeTypeChange = (key: string) => {
    setAgeType(key);
    setAgeModel(key);
  };

  const handleAddEntry = () => {
    const currentValues = form.getFieldsValue();

    setFormData((prevData) =>
      prevData.map((item) => ({
        ...item,
        age: currentValues[`age[${item.id}]`],
        SBPOperator: currentValues[`SBPOperator[${item.id}]`],
        SBPStart: currentValues[`SBPStart[${item.id}]`],
        SBPEnd: currentValues[`SBPEnd[${item.id}]`],
        DBPOperator: currentValues[`DBPOperator[${item.id}]`],
        DBPStart: currentValues[`DBPStart[${item.id}]`],
        DBPEnd: currentValues[`DBPEnd[${item.id}]`],
        classification: currentValues[`classification[${item.id}]`],
        conclusion: currentValues[`conclusion[${item.id}]`],
        recommendation: currentValues[`recommendation[${item.id}]`],
        radioSelection: currentValues[`radioSelection[${item.id}]`]
      }))
    );

    setFormData((prevData) => [
      { id: 1, isEditable: true },
      ...prevData.map((item) => ({ ...item, id: item.id + 1 }))
    ]);
  };

  const handleEdit = (id: number) => {
    setFormData((prevData) =>
      prevData.map((data) =>
        data.id === id ? { ...data, isEditable: true } : data
      )
    );
  };

  const handleDbpOperator = (id: number, value: string) => {
    setDbpOperatorValues((prev) => ({
      ...prev,
      [`DBPOperator[${id}]`]: value
    }));
    form.setFieldValue(`DBPStart[${id}]`, '');
    form.setFieldValue(`DBPEnd[${id}]`, '');
  };

  const handleSbpOperator = (id: number, value: string) => {
    setSbpOperatorValues((prev) => ({
      ...prev,
      [`SBPOperator[${id}]`]: value
    }));
    form.setFieldValue(`SBPStart[${id}]`, '');
    form.setFieldValue(`SBPEnd[${id}]`, '');
  };

  const onFinish: FormProps<FieldType>['onFinish'] = async (values) => {
    let newAgeType = ageType;
    if (model === 'WEIGHT') {
      showAge = true;
      newAgeType = 'FROM_0_LESS_THAN_5';
    } else if (model === 'WEIGHT_5_19') {
      showAge = true;
      newAgeType = 'FROM_5_LESS_THAN_19';
    } else if (model === 'HEIGHT_5_19') {
      showAge = true;
      newAgeType = ageType;
    }
    const formattedData = formData.map((data) => ({
      model,
      ageType: showAge ? newAgeType : values[`age[${data.id}]`],
      indicatorFrom: values[`SBPOperator[${data.id}]`],
      valueFrom: values[`SBPStart[${data.id}]`],
      valueTo: values[`SBPEnd[${data.id}]`],
      indicatorTo: values[`DBPOperator[${data.id}]`],
      valueOneFrom: values[`DBPStart[${data.id}]`],
      valueOneTo: values[`DBPEnd[${data.id}]`],
      type: values[`classification[${data.id}]`],
      conclusion: values[`conclusion[${data.id}]`],
      recommend: values[`recommendation[${data.id}]`],
      indicatorAnd: values[`radioSelection[${data.id}]`],
      gender: showGender ? gender : null
    }));
    if (model === 'BMI') {
      if (ageType === 'EQUAL_MORE_THAN_70' || ageType === 'FROM_20_LESS_THEN_70') {
        await dispatch(
          updateConclusionRecommendation({
            model: model,
            ageType: showAge ? newAgeType : undefined,
            items: JSON.stringify(formattedData)
          })
        );
      }
      else {
        await dispatch(
          updateConclusionRecommendation({
            model: model,
            gender: showGender ? gender : undefined,
            ageType: showAge ? newAgeType : undefined,
            items: JSON.stringify(formattedData)
          })
        );
      }
    } else {
      await dispatch(
        updateConclusionRecommendation({
          model: model,
          gender: showGender ? gender : undefined,
          ageType: showAge ? newAgeType : undefined,
          items: JSON.stringify(formattedData)
        })
      );
    }
    fetchData();
  };

  const onFinishFailed: FormProps<FieldType>['onFinishFailed'] = (
    errorInfo
  ) => {
    console.log('Failed:', errorInfo);
  };

  const handleDelete = (id: number) => {
    modal.confirm({
      title: 'Xóa Kết luận và Khuyến nghị',
      content: (
        <em>
          Bạn có chắc chắn muốn xóa kết luận và khuyến nghị này không? <br />
          Thông tin đã xóa sẽ không thể lấy lại được.
        </em>
      ),
      okText: 'Xác nhận',
      width: 500,
      cancelText: 'Hủy',
      onOk: () =>
        setFormData((prevData) => prevData.filter((data) => data.id !== id))
    });
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (model) {
      dispatch(getConclusionDropdown(model));
    }
  }, [dispatch, model]);

  useEffect(() => {
    const formValues: Record<string, any> = formData.reduce(
      (acc, curr) => {
        acc[`age[${curr.id}]`] = curr.age;
        acc[`SBPOperator[${curr.id}]`] = curr.SBPOperator;
        acc[`SBPStart[${curr.id}]`] = curr.SBPStart;
        acc[`SBPEnd[${curr.id}]`] = curr.SBPEnd;
        acc[`DBPOperator[${curr.id}]`] = curr.DBPOperator;
        acc[`DBPStart[${curr.id}]`] = curr.DBPStart;
        acc[`DBPEnd[${curr.id}]`] = curr.DBPEnd;
        acc[`classification[${curr.id}]`] = curr.classification;
        acc[`conclusion[${curr.id}]`] = curr.conclusion;
        acc[`recommendation[${curr.id}]`] = curr.recommendation;
        acc[`radioSelection[${curr.id}]`] = curr.radioSelection;
        return acc;
      },
      {} as Record<string, any>
    );

    form.setFieldsValue(formValues);

    const newSbpOperatorValues = formData.reduce(
      (acc, curr) => {
        acc[`SBPOperator[${curr.id}]`] = curr.SBPOperator ?? '';
        return acc;
      },
      {} as Record<string, string>
    );

    setSbpOperatorValues(newSbpOperatorValues);

    const newDbpOperatorValues = formData.reduce(
      (acc, curr) => {
        acc[`DBPOperator[${curr.id}]`] = curr.DBPOperator ?? '';
        return acc;
      },
      {} as Record<string, string>
    );

    setDbpOperatorValues(newDbpOperatorValues);
  }, [form, formData]);

  useEffect(() => {
    setFormData(
      conclusionRecommendationList.map((val, index) => ({
        id: index + 1,
        age: val.ageType,
        SBPOperator: val.indicatorFrom,
        SBPStart: val.valueFrom,
        SBPEnd: val.valueTo,
        DBPOperator: val.indicatorTo,
        DBPStart: val.valueOneFrom,
        DBPEnd: val.valueOneTo,
        classification: val.type,
        conclusion: val.conclusion,
        recommendation: val.recommend,
        radioSelection: val.indicatorAnd,
        isEditable: false
      }))
    );
  }, [conclusionRecommendationList]);

  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([
          {
            type: messageInfo.type,
            message: messageInfo.message,
            description: messageInfo.description
          }
        ])
      );

      dispatch(resetMessages());
    }
  }, [dispatch, messageInfo]);

  useBeforeUnload((e) => {
    const isDirty =
      conclusionRecommendationList.length !== formData.length ||
      formData.some((item) => item.isEditable);

    if (isDirty) e.preventDefault();
  });

  return (
    <Fragment>
      {showAge && (
        < Tabs
          css={tabStyle}
          defaultActiveKey='FROM_5_LESS_THAN_12'
          items={itemFollowAges}
          onChange={(key) => handleAgeTypeChange(key)}
        />
      )}
      {showGender && (
        <Tabs
          css={tabStyle}
          defaultActiveKey='nam'
          items={items}
          onChange={(key) => setGender(key)}
        />
      )}
      {creatable && (
        <Button
          icon={<PlusOutlined />}
          type='primary'
          onClick={handleAddEntry}
          css={addUnitBtnStyles}
        >
          Thêm kết luận và khuyến nghị
        </Button>
      )}
      <Spin spinning={loading}>
        <div css={contentAreaStyles}>
          {formData.length > 0 || conclusionRecommendationList.length > 0 ? (
            <Form
              css={formStyles}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              labelCol={{ span: 8 }}
              form={form}
            >
              {formData.map((unit, index) => (
                <div key={unit.id}>
                  <Row wrap={false} gutter={16}>
                    <Col flex={1} css={colStyles(unit.isEditable)}>
                      <BloodPressureDetails
                        unit={unit}
                        model={model}
                        selectedKey={selectedKey}
                        ageModel={ageModel}
                        typeOptions={typeOptions}
                        itemFollowAges={itemFollowAges}
                        operatorsOptions={operatorsOptions}
                      />
                    </Col>
                    <Col flex={1} css={hideDetailStyles(unit.isEditable)}>
                      {!excludeOptionAgeRelated(
                        selectedKey as ConclusionRecommendationKey
                      ) && (
                          <>
                            <Form.Item<FieldType>
                              name={`age[${unit.id}]`}
                              label='Tuổi'
                              rules={[
                                {
                                  required: true,
                                  message: 'Vui lòng chọn lứa tuổi!'
                                }
                              ]}
                            >
                              <Select
                                css={inputStyles}
                                allowClear
                                placeholder='Chọn Lứa tuổi'
                                optionFilterProp='label'
                                options={ageOptions}
                              />
                            </Form.Item>
                          </>
                        )}
                      {isIndicatorOne(
                        selectedKey as ConclusionRecommendationKey
                      ) && (
                          <>
                            <Form.Item<FieldType>
                              label={bloodPressureLabelOne.label}
                              name={`SBP[${unit.id}]`}
                              required
                            >
                              <Row gutter={10}>
                                <Col span={8}>
                                  <Form.Item
                                    name={`SBPOperator[${unit.id}]`}
                                    rules={[
                                      {
                                        required: true,
                                        message: 'Vui lòng chọn Khoảng!'
                                      }
                                    ]}
                                    noStyle
                                  >
                                    <Select
                                      allowClear
                                      optionFilterProp='label'
                                      placeholder='Chọn khoảng'
                                      options={operatorsOptions}
                                      onChange={(value) =>
                                        handleSbpOperator(unit.id, value)
                                      }
                                    />
                                  </Form.Item>
                                </Col>
                                <Col
                                  span={
                                    sbpOperatorValues[
                                      `SBPOperator[${unit.id}]`
                                    ] === 'ABOUT'
                                      ? 16
                                      : 8
                                  }
                                >
                                  <Row align='middle' justify='center'>
                                    <Col
                                      span={
                                        sbpOperatorValues[
                                          `SBPOperator[${unit.id}]`
                                        ] === 'ABOUT'
                                          ? 11
                                          : 24
                                      }
                                    >
                                      <Form.Item
                                        name={`SBPStart[${unit.id}]`}
                                        rules={[
                                          {
                                            required: true,
                                            message:
                                              'Vui lòng nhập chỉ số bắt đầu!'
                                          }
                                        ]}
                                        noStyle
                                      >
                                        <InputNumber
                                          css={inputStyles}
                                          placeholder={
                                            sbpOperatorValues[
                                              `SBPOperator[${unit.id}]`
                                            ] === 'ABOUT'
                                              ? 'Từ'
                                              : ''
                                          }
                                          suffix={bloodPressureLabelOne.unit}
                                        />
                                      </Form.Item>
                                    </Col>
                                    {sbpOperatorValues[
                                      `SBPOperator[${unit.id}]`
                                    ] === 'ABOUT' && (
                                        <>
                                          <Col span={2} css={dashColStyles}>
                                            -
                                          </Col>
                                          <Col span={11}>
                                            <Form.Item
                                              name={`SBPEnd[${unit.id}]`}
                                              rules={[
                                                {
                                                  required: true,
                                                  message:
                                                    'Vui lòng nhập chỉ số kết thúc!'
                                                }
                                              ]}
                                              noStyle
                                            >
                                              <InputNumber
                                                css={inputStyles}
                                                placeholder='Đến'
                                                suffix={bloodPressureLabelOne.unit}
                                              />
                                            </Form.Item>
                                          </Col>
                                        </>
                                      )}
                                  </Row>
                                </Col>
                              </Row>
                            </Form.Item>
                            {isIndicatorTwo(
                              selectedKey as ConclusionRecommendationKey
                            ) && (
                                <Row>
                                  <Col span={8}></Col>
                                  <Col>
                                    <Form.Item<FieldType>
                                      name={`radioSelection[${unit.id}]`}
                                      rules={[
                                        {
                                          required: true,
                                          message: 'Vui lòng chọn!'
                                        }
                                      ]}
                                    >
                                      <Radio.Group>
                                        <Radio value='and'>Và</Radio>
                                        <Radio value='or'>Và/ Hoặc</Radio>
                                      </Radio.Group>
                                    </Form.Item>
                                  </Col>
                                </Row>
                              )}
                          </>
                        )}
                      {isIndicatorTwo(
                        selectedKey as ConclusionRecommendationKey
                      ) && (
                          <>
                            <Form.Item<FieldType>
                              label={bloodPressureLabelTwo.label}
                              name={`DBP[${unit.id}]`}
                              required
                            >
                              <Row gutter={10}>
                                <Col span={8}>
                                  <Form.Item
                                    name={`DBPOperator[${unit.id}]`}
                                    rules={[
                                      {
                                        required: true,
                                        message: 'Vui lòng chọn Khoảng!'
                                      }
                                    ]}
                                    noStyle
                                  >
                                    <Select
                                      allowClear
                                      optionFilterProp='label'
                                      options={operatorsOptions}
                                      placeholder='Chọn khoảng'
                                      onChange={(value) =>
                                        handleDbpOperator(unit.id, value)
                                      }
                                    />
                                  </Form.Item>
                                </Col>
                                <Col
                                  span={
                                    dbpOperatorValues[
                                      `DBPOperator[${unit.id}]`
                                    ] === 'ABOUT'
                                      ? 16
                                      : 8
                                  }
                                >
                                  <Row align='middle' justify='center'>
                                    <Col
                                      span={
                                        dbpOperatorValues[
                                          `DBPOperator[${unit.id}]`
                                        ] === 'ABOUT'
                                          ? 11
                                          : 24
                                      }
                                    >
                                      <Form.Item
                                        name={`DBPStart[${unit.id}]`}
                                        rules={[
                                          {
                                            required: true,
                                            message:
                                              'Vui lòng nhập chỉ số bắt đầu!'
                                          }
                                        ]}
                                        noStyle
                                      >
                                        <InputNumber
                                          css={inputStyles}
                                          placeholder={
                                            dbpOperatorValues[
                                              `DBPOperator[${unit.id}]`
                                            ] === 'ABOUT'
                                              ? 'Từ'
                                              : ''
                                          }
                                          suffix={bloodPressureLabelOne.unit}
                                        />
                                      </Form.Item>
                                    </Col>
                                    {dbpOperatorValues[
                                      `DBPOperator[${unit.id}]`
                                    ] === 'ABOUT' && (
                                        <>
                                          <Col span={2} css={dashColStyles}>
                                            -
                                          </Col>
                                          <Col span={11}>
                                            <Form.Item
                                              name={`DBPEnd[${unit.id}]`}
                                              rules={[
                                                {
                                                  required: true,
                                                  message:
                                                    'Vui lòng nhập chỉ số kết thúc!'
                                                }
                                              ]}
                                              noStyle
                                            >
                                              <InputNumber
                                                css={inputStyles}
                                                placeholder='Đến'
                                                suffix={bloodPressureLabelOne.unit}
                                              />
                                            </Form.Item>
                                          </Col>
                                        </>
                                      )}
                                  </Row>
                                </Col>
                              </Row>
                            </Form.Item>
                          </>
                        )}
                      {!excludeOptionAgeRelated(
                        selectedKey as ConclusionRecommendationKey
                      ) && (
                          <>
                            <Form.Item<FieldType>
                              label='Phân loại'
                              name={`classification[${unit.id}]`}
                              rules={[
                                {
                                  required: true,
                                  message: 'Vui lòng chọn phân loại huyết áp!'
                                }
                              ]}
                            >
                              <Select
                                css={inputStyles}
                                allowClear
                                optionFilterProp='label'
                                placeholder='Chọn phân loại'
                                options={typeOptions}
                              />
                            </Form.Item>
                          </>
                        )}
                      {excludeOptionAgeRelated(
                        selectedKey as ConclusionRecommendationKey
                      ) && (
                          <>
                            {optionAgeType(
                              ageModel as ConclusionRecommendationAgeKey
                            ) && (
                                <>
                                  <Form.Item<FieldType>
                                    label={bloodPressureLabelOne.label}
                                    name={`SBP[${unit.id}]`}
                                    required
                                  >
                                    <Row gutter={10}>
                                      <Col span={8}>
                                        <Form.Item
                                          name={`SBPOperator[${unit.id}]`}
                                          rules={[
                                            {
                                              required: true,
                                              message: 'Vui lòng chọn Khoảng!'
                                            }
                                          ]}
                                          noStyle
                                        >
                                          <Select
                                            allowClear
                                            optionFilterProp='label'
                                            placeholder='Chọn khoảng'
                                            options={operatorsOptions}
                                            onChange={(value) =>
                                              handleSbpOperator(unit.id, value)
                                            }
                                          />
                                        </Form.Item>
                                      </Col>
                                      <Col
                                        span={
                                          sbpOperatorValues[
                                            `SBPOperator[${unit.id}]`
                                          ] === 'ABOUT'
                                            ? 16
                                            : 8
                                        }
                                      >
                                        <Row align='middle' justify='center'>
                                          <Col
                                            span={
                                              sbpOperatorValues[
                                                `SBPOperator[${unit.id}]`
                                              ] === 'ABOUT'
                                                ? 11
                                                : 24
                                            }
                                          >
                                            <Form.Item
                                              name={`SBPStart[${unit.id}]`}
                                              rules={[
                                                {
                                                  required: true,
                                                  message:
                                                    'Vui lòng nhập chỉ số bắt đầu!'
                                                }
                                              ]}
                                              noStyle
                                            >
                                              <InputNumber
                                                css={inputStyles}
                                                placeholder={
                                                  sbpOperatorValues[
                                                    `SBPOperator[${unit.id}]`
                                                  ] === 'ABOUT'
                                                    ? 'Từ'
                                                    : ''
                                                }
                                                suffix={bloodPressureLabelOne.unit}
                                              />
                                            </Form.Item>
                                          </Col>
                                          {sbpOperatorValues[
                                            `SBPOperator[${unit.id}]`
                                          ] === 'ABOUT' && (
                                              <>
                                                <Col span={2} css={dashColStyles}>
                                                  -
                                                </Col>
                                                <Col span={11}>
                                                  <Form.Item
                                                    name={`SBPEnd[${unit.id}]`}
                                                    rules={[
                                                      {
                                                        required: true,
                                                        message:
                                                          'Vui lòng nhập chỉ số kết thúc!'
                                                      }
                                                    ]}
                                                    noStyle
                                                  >
                                                    <InputNumber
                                                      css={inputStyles}
                                                      placeholder='Đến'
                                                      suffix={
                                                        bloodPressureLabelOne.unit
                                                      }
                                                    />
                                                  </Form.Item>
                                                </Col>
                                              </>
                                            )}
                                        </Row>
                                      </Col>
                                    </Row>
                                  </Form.Item>
                                </>
                              )}
                            <Form.Item<FieldType>
                              label='Phân loại'
                              name={`classification[${unit.id}]`}
                              rules={[
                                {
                                  required: true,
                                  message: 'Vui lòng chọn phân loại huyết áp!'
                                }
                              ]}
                            >
                              <Select
                                css={inputStyles}
                                allowClear
                                optionFilterProp='label'
                                placeholder='Chọn phân loại'
                                options={typeOptions}
                              />
                            </Form.Item>
                          </>
                        )}
                      <Form.Item<FieldType>
                        label='Kết luận'
                        name={`conclusion[${unit.id}]`}
                      >
                        <ReactQuill value={unit.conclusion}
                          modules={modules}
                          formats={formats}
                        />
                      </Form.Item>
                      <Form.Item<FieldType>
                        label='Khuyến nghị'
                        name={`recommendation[${unit.id}]`}
                        rules={[
                          {
                            required: true,
                            message: 'Vui lòng nhập Khuyến nghị!',
                            transform: (value) =>
                              value.replace(/<(.|\n)*?>/g, '').trim(),
                            validateTrigger: 'onBlur'
                          }
                        ]}
                      >
                        <ReactQuill value={unit.recommendation}
                          modules={modules}
                          formats={formats}
                        />
                      </Form.Item>
                    </Col>

                    <Col>
                      <Space>
                        {!unit.isEditable && unit.id && updatable && (
                          <Tooltip title='Cập nhật'>
                            <ButtonAnt
                              icon={<EditOutlined />}
                              type='primary'
                              onClick={() => handleEdit(unit.id)}
                            />
                          </Tooltip>
                        )}
                        {deletable && (
                          <Tooltip title='Xoá'>
                            <ButtonAnt
                              icon={<DeleteOutlined />}
                              type='primary'
                              danger
                              onClick={() => handleDelete(unit.id)}
                            />
                          </Tooltip>
                        )}
                        {!(!unit.isEditable && unit.id) && (
                          <span css={unitPlaceholderStyles} />
                        )}
                      </Space>
                    </Col>
                  </Row>

                  {index < formData.length - 1 && (
                    <Divider css={dividerStyles} />
                  )}
                </div>
              ))}
              {(conclusionRecommendationList.length > 0 ||
                formData.length > 0) && (
                  <Row justify='end' css={footerButtonGroupStyles}>
                    <Col>
                      <Space>
                        <Button
                          shape='round'
                          onClick={fetchData}
                          disabled={loading}
                        >
                          Huỷ
                        </Button>
                        <Button
                          htmlType='submit'
                          type='primary'
                          shape='round'
                          disabled={loading}
                        >
                          Lưu
                        </Button>
                      </Space>
                    </Col>
                  </Row>
                )}
            </Form>
          ) : (
            <Empty
              description='Chưa có kết luận và khuyến nghị nào'
              css={emptyStyles}
            />
          )}
        </div>
      </Spin>
    </Fragment>
  );
};

type BloodPressureDetailsProps = {
  unit: FormData;
  model: string;
  ageModel: string;
  selectedKey: string;
  typeOptions: any[];
  itemFollowAges: any[];
  operatorsOptions: any[];
};
const BloodPressureDetails = ({
  unit,
  model,
  selectedKey,
  ageModel,
  typeOptions,
  itemFollowAges,
  operatorsOptions
}: BloodPressureDetailsProps) => {
  const bloodPressureLabelOne = useMemo(
    () => valueSelectedModelOne(selectedKey as ConclusionRecommendationKey),
    [selectedKey]
  );
  const bloodPressureLabelTwo = useMemo(
    () => valueSelectedModelTwo(selectedKey as ConclusionRecommendationKey),
    [selectedKey]
  );
  return (
    <>
      {!notShowAge(selectedKey as ConclusionRecommendationKey) && (
        <Row gutter={16}>
          <Col span={7}>Tuổi: </Col>
          {model === 'WEIGHT' ? (
            <Col>{'<5 tuổi'}</Col>
          ) : (
            <Col>
              {itemFollowAges.find((item) => item.key === unit.age)?.label}
            </Col>
          )}
        </Row>
      )}

      {isIndicatorOne(selectedKey as ConclusionRecommendationKey) && (
        <>
          <Row gutter={16}>
            <Col span={7}>{bloodPressureLabelOne.label}: </Col>
            <Col>
              {operatorsOptions.find((item) => item.value === unit.SBPOperator)
                ?.label +
                (unit.SBPOperator === 'ABOUT'
                  ? ` ${unit.SBPStart} đến ${unit.SBPEnd}`
                  : ` ${unit.SBPStart}`)}
              &nbsp;{bloodPressureLabelOne.unit}
            </Col>
          </Row>
        </>
      )}
      {excludeOptionAgeRelated(selectedKey as ConclusionRecommendationKey) && (
        <>
          {optionAgeType(ageModel as ConclusionRecommendationAgeKey) && (
            <>
              <Row gutter={16}>
                <Col span={7}>{bloodPressureLabelOne.label}: </Col>
                <Col>
                  {operatorsOptions.find(
                    (item) => item.value === unit.SBPOperator
                  )?.label +
                    (unit.SBPOperator === 'ABOUT'
                      ? ` ${unit.SBPStart} đến ${unit.SBPEnd}`
                      : ` ${unit.SBPStart}`)}
                  &nbsp;{bloodPressureLabelOne.unit}
                </Col>
              </Row>
            </>
          )}
        </>
      )}

      {bloodPressureInput(selectedKey as ConclusionRecommendationKey) && (
        <>
          <Row gutter={16}>
            <Col span={7}></Col>
            <Col>
              <Radio.Group value={unit.radioSelection}>
                <Radio value='and'>Và</Radio>
                <Radio value='or'>Và/ Hoặc</Radio>
              </Radio.Group>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={7}>{bloodPressureLabelTwo.label}: </Col>
            <Col>
              {operatorsOptions.find((item) => item.value === unit.DBPOperator)
                ?.label +
                (unit.DBPOperator === 'ABOUT'
                  ? ` ${unit.DBPStart} đến ${unit.DBPEnd}`
                  : ` ${unit.DBPStart}`)}
              &nbsp;{bloodPressureLabelTwo.unit}
            </Col>
          </Row>
        </>
      )}
      <Row gutter={16}>
        <Col span={7}>Phân loại: </Col>
        <Col>
          {
            typeOptions.find((item) => item.value === unit.classification)
              ?.label
          }
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={7}>Kết luận: </Col>
        <Col
          dangerouslySetInnerHTML={{
            __html: unit.conclusion || ''
          }}
        />
      </Row>
      <Row gutter={16}>
        <Col span={7}>Khuyến nghị: </Col>
        <Col
          dangerouslySetInnerHTML={{
            __html: unit.recommendation || ''
          }}
        />
      </Row>
    </>
  );
};

const contentAreaStyles = css`
  margin-left: 1.4rem;
  background: var(--white-color);
  padding: 2rem;
  border-radius: 0.6rem;
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  overflow-y: auto;
  margin-bottom: 2rem;
`;

const formStyles = css`
  max-width: 100rem;
  .ant-form-item-label {
    text-align: start;
  }
`;

const addUnitBtnStyles = css`
  margin: 0 0 1.4rem 1.4rem;
`;

const dividerStyles = css`
  margin: 0.6rem 0 3rem 0;
`;

const footerButtonGroupStyles = css`
  margin-top: 2.4rem;
  button {
    width: 8rem;
  }
`;

const unitPlaceholderStyles = css`
  display: inline-block;
  width: 3.2rem;
`;

const inputStyles = css`
  width: 100%;
`;

const dashColStyles = css`
  text-align: center;
`;

const colStyles = (isEditable: boolean) => css`
  margin-bottom: 1.6rem;
  flex-direction: column;
  gap: 1rem;
  display: ${!isEditable ? 'flex' : 'none'};
`;

const hideDetailStyles = (hide: boolean) => css`
  display: ${hide ? 'block' : 'none'};
`;

const tabStyle = css`
  margin-left: 1.4rem;
  border-radius: 0.6rem;
  background: var(--white-color);
  padding: 0 1.4rem;
  margin-bottom: 1.4rem;
  .ant-tabs-nav {
    margin-bottom: 0;
  }
`;

const emptyStyles = css`
  min-height: calc(100vh - 44rem);
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
`;
